import { newDesignSettings } from './portfolioDesign';
import {
    OPTIMIZER_V7_PROVIDER_DIGEST,
    OPTIMIZER_V7_PROVIDER_MODEL_BUILD,
    OPTIMIZER_V7_PROVIDER_ROUTE,
} from './optimizerGenericGroupContract';

// Manual chooses one objective. Meta may choose an explicit weighted balance;
// both serialize the same construction document to the same solver.
export const MANUAL_OBJECTIVES = Object.freeze({
    AlphaScore: { goal: 'max_alpha', label: 'Max Alpha-Score', help: 'Maximize the trade-idea Alpha-Score within your enabled limits.' },
    MinFactorVol: { goal: 'min_factor_vol', label: 'Min Factor Volatility', help: 'Minimize modeled factor risk while respecting your conviction and trading limits.' },
    MaxIdioShare: { goal: 'max_idio_share', label: 'Max Idiosyncratic Share · experimental', help: 'Increase specific variance as a share of total variance. This may reduce factor risk or increase specific risk; it does not forecast returns. A global optimum is not guaranteed.' },
    MaxIdioVol: { goal: 'max_idio_vol', label: 'Max Idiosyncratic Volatility · experimental', help: 'Increase absolute security-specific risk. Review total risk and concentration limits. A global optimum is not guaranteed.' },
    MinTurnover: { goal: 'min_turnover', label: 'Min Turnover', help: 'Minimize buys plus sells while meeting your enabled limits. If the baseline already meets them, no trading may be needed.' },
});

// Max Sharpe runs on its own contract, not as a construction balance. Its
// expected returns follow Grinold's rule from the trade ideas: skill × specific
// risk × idea ÷ the ideas' root mean square. Skill scales every expected return
// alike, so it changes the reported return and Sharpe ratio, never the positions.
export const MAX_SHARPE_OBJECTIVE = 'SharpeRatio';
export const MAX_SHARPE_LABEL = 'Max Sharpe';
export const MAX_SHARPE_HELP = 'Maximize expected return within your total volatility limit. '
    + 'Expected returns come from your trade ideas, scaled by each name’s specific risk and your skill. '
    + 'With these returns Max Sharpe builds the same portfolio as Max Alpha-Score under the same limits; '
    + 'skill changes the reported expected return and Sharpe ratio, not the positions.';
export const TRADE_IDEA_EXPECTED_RETURN_POLICY = 'trade-idea-ic-specific-risk.v1';
export const DEFAULT_INFORMATION_COEFFICIENT = 0.05;

export function tradeIdeaExpectedReturnPolicy(informationCoefficient = DEFAULT_INFORMATION_COEFFICIENT) {
    return { name: TRADE_IDEA_EXPECTED_RETURN_POLICY, information_coefficient: informationCoefficient };
}

export function objectiveWeights(goal) {
    return { conviction: goal === 'max_alpha' ? 1 : 0, risk: goal === 'max_alpha' ? 0 : 1, turnover: 0, tracking: 0 };
}

export function manualDesign(objective = 'AlphaScore', previous = null) {
    const goal = MANUAL_OBJECTIVES[objective]?.goal;
    if (!goal) throw new Error('Choose an available objective.');
    const base = previous ?? newDesignSettings().design;
    return {
        ...base, goal, construction: null, preferences: objectiveWeights(goal),
        mandate: previous?.mandate ?? {
            ...base.mandate,
            score_floor: objective === 'AlphaScore' ? null : { mode: 'retention_pct', value: 95 },
            preserve_sides: false,
            position_band_pct: null,
            preserve_gross: false, preserve_net: false,
        },
    };
}

export function objectiveForDesign(design) {
    const weights = design.construction ?? design.preferences;
    const pure = objectiveWeights(design.goal);
    const matches = weights && Object.keys(pure).every((key) =>
        pure[key] === 0 ? weights[key] === 0 : weights[key] > 0);
    return matches ? Object.keys(MANUAL_OBJECTIVES).find((key) => MANUAL_OBJECTIVES[key].goal === design.goal) ?? 'PortfolioDesign' : 'PortfolioDesign';
}

export function designForManualRun(objective, constraints) {
    if (objective === MAX_SHARPE_OBJECTIVE) return null;
    const stored = constraints?.design;
    if (objective === 'PortfolioDesign') {
        if (!stored) throw new Error('This portfolio is missing its saved construction settings. Reload it before running.');
        return stored;
    }
    if (objective === 'AlphaScore' && !stored) return null;
    // Loaded blends retain their exact weights until an explicit objective edit.
    return stored ?? manualDesign(objective);
}

export const DESIGN_CONSTRAINT_SCHEMA = 'optimizer-constraints.v6';
export const DESIGN_CONSTRAINT_DIGEST = '0719998052979a30d822a39c5469d45c3eecc9fd2f0e95767b93673614af8d9a';

export function expectedRunProvider(command, provider) {
    if (command.objective === MAX_SHARPE_OBJECTIVE) {
        return { ...provider, contract_digest: OPTIMIZER_V7_PROVIDER_DIGEST,
            route: OPTIMIZER_V7_PROVIDER_ROUTE, model_build: OPTIMIZER_V7_PROVIDER_MODEL_BUILD };
    }
    if (!command.design) return provider;
    return { ...provider,
        contract_digest: '0177a642e632dd2edd30efbc5663fac254504ceade79049f805f81d49a84f689',
        route: 'in-process:optimizer-design-v1', model_build: 'optimizer-portfolio-design.v1',
        constraint_schema_version: DESIGN_CONSTRAINT_SCHEMA, constraint_contract_digest: DESIGN_CONSTRAINT_DIGEST,
    };
}

// Copy follows the metric already displayed. Receipt objective is immutable;
// editing the next run's objective must not relabel the previous result.
export function optimizerAlphaScorePresentation({ receiptDisplayActive, receiptObjective, objective = 'AlphaScore', design }) {
    const weighted = receiptDisplayActive ? receiptObjective === 'PortfolioDesign'
        : Boolean(design || (objective !== 'AlphaScore' && objective !== MAX_SHARPE_OBJECTIVE));
    return weighted ? {
        label: 'Risk-weighted Alpha-Score ($)',
        tooltip: 'Sum of signed position value × trade idea × specific risk. This is the score used by the risk-weighted Alpha-Score floor; it is a conviction score, not a return forecast.',
    } : {
        label: 'Trade-idea score ($)',
        tooltip: 'Sum of signed position value × raw trade idea. The risk-weighted Alpha-Score floor additionally weights each trade idea by its specific risk.',
    };
}
